<template>
  <div class="Step0Form">
    <div
      v-if="showAllYearPassed"
      class="top-msg"
    >
      <h2>{{ repricingAnswers.product?.productName }}</h2>
      <p>{{ t('travel.passed-all-year-date') }}</p>
      <CardInfoComponent
        v-if="allYearPassedDateAlert"
        :icon="allYearPassedDateAlert.icon"
      >
        {{ allYearPassedDateAlert.label }}
      </CardInfoComponent>
    </div>

    <div
      v-if="showPassedDate"
      class="top-msg"
    >
      <h2>{{ repricingAnswers.product?.productName }}</h2>
      <p>{{ t('travel.step0passed-date') }}</p>
    </div>

    <!-- ▼ DESTINATION TYPE ▼ -->
    <BaseSelect
      id="destination-type"
      :label="t(`copy_travel_destination-type_form-title-${forWhom}`, forWhomPeople)"
      :items="destinationTypesOptions"
      v-model="destinationType"
      :rules="[validationRules.required]"
    />
    <!-- ▲ DESTINATION TYPE ▲ -->

    <!-- ▼ DEPARTURE DATE ▼ -->
    <DatePickerComponent
      v-if="!isSeveralTravelsType"
      id="departure-date"
      :label="t(`copy_travel_duration_form_departure-date-${forWhom}`, forWhomPeople)"
      :placeholder="t('copy_travel_duration_form_departure-date-placeholder')"
      v-model="repricingAnswers.departureDate"
      :datePickerVisible="true"
      :min-date="minDateDeparture"
      :max-date="maxDateDeparture"
      :rules="[required]"
    />
    <!-- ▲ DEPARTURE DATE ▲ -->

    <!-- ▼ RETURN DATE ▼ -->
    <DatePickerComponent
      v-if="!isSeveralTravelsType"
      id="return-date"
      :label="t(`copy_travel_duration_form_return-date-${forWhom}`, forWhomPeople)"
      :placeholder="t('copy_travel_duration_form_return-date-placeholder')"
      v-model="repricingAnswers.returnDate"
      :datePickerVisible="true"
      :min-date="minDateReturn"
      :max-date="maxDateReturn"
      :rules="[required]"
    />
    <!-- ▲ RETURN DATE ▲ -->

    <div class="actions">
      <BaseButton
        @click="simulateRepricing"
        :ariaDisabled="!isValidForm || !changeData"
      >
        {{ t('rater.base.submit') }}
      </BaseButton>

      <template v-if="askToCalculateInsurance && price && !changeData">
        <v-divider class="divider-blue" />

        <TotalAmountComponent
          :label="t('hiring.common.step-0.price-updated-label')"
          :amount="price"
          :currency="paymentUnits"
          :period="period"
        />

        <BaseButton @click="repricing">
          {{ t('hiring.common.step-0.price-updated-button') }}
        </BaseButton>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import type { PricePeriodType } from '@/modules/common/common/models/APP/types'
  import TotalAmountComponent from '@/modules/common/hiring/components/TotalAmountComponent.vue'
  import { useRepricing } from '@/modules/common/hiring/composables/useRepricing'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import type { FormAnswersType } from '@/modules/core/models/types'
  import type TravelConfigurations from '@/modules/travel/common/models/APP/TravelConfigurations'
  import type TravelInsurance from '@/modules/travel/common/models/APP/TravelInsurance'
  import { addDaysToDate, addYearsToDate } from '@/utils/date.utils'
  import { computed, onBeforeMount, ref, watch, type Ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import TravelHiringAnswers from '../../models/APP/TravelHiringAnswers'
  import { useTravelStore } from '../../stores'

  const MIN_DATE = addDaysToDate(0)

  const { t } = useI18n()
  const { required, yesNoRequired } = useRules()
  const travelStore = useTravelStore()
  const errorManager = useErrorManagerComposable()

  interface PropsInterface {
    nextForm: string[]
    answers: FormAnswersType
    configurations: TravelConfigurations
    isValidForm: boolean
    showTitle?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const emit = defineEmits(['repricing'])

  const repricingAnswers = ref(new TravelHiringAnswers(props.answers)) as Ref<TravelHiringAnswers>
  const {
    price,
    changeData,
    paymentUnits,
    askToCalculateInsurance,
    initializeCommon,
    simulateRepricingCommon,
    repricingCommon
  } = useRepricing<TravelHiringAnswers, TravelInsurance>(repricingAnswers, props.configurations)
  const showAllYearPassed = ref(false)

  const forWhomPeople = computed(() => repricingAnswers.value.forWhomPeople ?? 1)
  const forWhom = computed(() => repricingAnswers.value.forWhom ?? 'me')
  const allYearPassedDateAlert = computed(() => props.configurations.tags.allYearPassedDateAlert)
  const showPassedDate = computed(() => {
    if (showAllYearPassed.value) return false
    const departureDate = new Date(props.answers.departureDate)
    const todayDate = new Date()
    departureDate.setHours(0, 0, 0, 0)
    todayDate.setHours(0, 0, 0, 0)
    return departureDate < todayDate
  })
  const destinationTypes = computed(() => props.configurations.destinationTypes)
  const insuranceTypes = computed(() => props.configurations.insuranceTypes)
  const isSeveralTravelsType = computed(
    () => insuranceTypes.value.several.id === String(props.answers.insuranceType)
  )
  const hasSportInsurance = computed(
    () =>
      insuranceTypes.value.studyAbroad.id === String(props.answers.insuranceType) &&
      repricingAnswers.value.sportInsurance
  )
  const destinationType = ref(String(props.answers.destinationType))
  const destinationTypesOptions = computed(() => {
    if (hasSportInsurance.value) {
      return props.configurations.destinationTypesOptions.filter((dto) =>
        [
          destinationTypes.value.europeAndMediterraneanCoastCountries.id,
          destinationTypes.value.restOfTheWorld.id
        ].includes(dto.value)
      )
    }
    const options = props.configurations.destinationTypesOptions.filter(
      (dto) =>
        ![destinationTypes.value.spainEuropeAndMediterraneanCoastCountries.id].includes(dto.value)
    )
    return options
  })

  const validationRules = {
    required,
    yesNoRequired
  }
  const period = computed(() => {
    const v =
      props.configurations.billingCycles?.[
        (repricingAnswers.value.billingCycle as PricePeriodType) ?? 'unique'
      ]?.textInPrice
    return v ? `/${v}` : ''
  })

  const minDateDeparture = computed(() => {
    const today = new Date(MIN_DATE)
    return new Date(today.getFullYear(), today.getMonth(), today.getDate())
  })

  const maxDateDeparture = computed(() => addYearsToDate(props.configurations.maxYearsDeparture))

  const minDateReturn = computed(() => {
    const d = new Date(repricingAnswers.value.departureDate || minDateDeparture.value)
    d.setDate(d.getDate() + 1)
    return d
  })

  const maxDateReturn = computed(() =>
    addYearsToDate(
      props.configurations.maxYearsReturn,
      repricingAnswers.value.departureDate || new Date()
    )
  )

  async function simulateRepricing() {
    try {
      repricingAnswers.value.destinationType = Number(destinationType.value)
      await simulateRepricingCommon(travelStore.getNewRaterPrice)
      setTimeout(() => {
        const modalElement = document.getElementsByClassName('modal-panel')?.[0]
        if (modalElement) {
          modalElement.scrollTo({
            top: modalElement.scrollHeight,
            behavior: 'smooth'
          })
        }
      }, 100)
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  async function repricing() {
    try {
      repricingAnswers.value.startDate = repricingAnswers.value.departureDate
      await repricingCommon(travelStore.getNewRaterPrice, travelStore.hireProduct) // ver si puedo evitar que se llame a raterprice 2 veces
      emit('repricing')
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  // function updatedSportInsurance() {
  //   if (
  //     repricingAnswers.value.sportInsurance &&
  //     ![
  //       props.configurations.destinationTypes.europeAndMediterraneanCoastCountries.id,
  //       props.configurations.destinationTypes.restOfTheWorld.id
  //     ].includes(destinationType.value!)
  //   ) {
  //     destinationType.value = ''
  //   }
  // }

  watch(
    () => [
      repricingAnswers.value.sportInsurance,
      destinationType.value,
      repricingAnswers.value.departureDate,
      repricingAnswers.value.returnDate
    ],
    () => (changeData.value = true),
    { deep: true }
  )

  onBeforeMount(() => {
    initializeCommon(repricingAnswers.value)
    if (isSeveralTravelsType.value) {
      const departureDate = new Date()
      departureDate.setHours(0, 0, 0, 0)
      repricingAnswers.value.departureDate.setHours(0, 0, 0, 0)
      if (repricingAnswers.value.departureDate < departureDate) {
        const returnDate = new Date()
        returnDate.setFullYear(returnDate.getFullYear() + 1)
        repricingAnswers.value.departureDate = departureDate
        repricingAnswers.value.returnDate = returnDate
        showAllYearPassed.value = true
      }
    }
  })
</script>

<style lang="scss" scoped>
  .Step0Form {
    width: $size-screen-w;
    max-width: $size-375;
    padding: 0 $spacing-l;
    margin: auto;

    .gl_form-title {
      font-size: $font-size-20;
      margin-bottom: $spacing-2xl;
    }

    .top-msg {
      margin-bottom: $spacing-28;

      h2 {
        color: $color-primary-dark;
        font-size: $font-size-xl;
      }

      p {
        color: $color-neutral;
        font-size: $font-size-l;
      }
    }
  }
</style>
